import { useNavigate } from '@tanstack/react-router'
import { CommandPalette as BaseCommandPalette } from '@trellis/roots/navigation'
import type { CommandItem } from '@trellis/roots/navigation'
import { FEATURE } from '@trellis/shared'
import type { FeatureKey } from '@trellis/shared'
import {
  Building2,
  FileText,
  LayoutDashboard,
  Package,
  Plus,
  Receipt,
  Settings,
  Shield,
  TrendingUp,
  Users,
} from 'lucide-react'

import { useCurrentTenantRole } from '@/hooks/useCurrentTenantRole'
import { filterByFeature, useFeatureFlags } from '@/hooks/useFeatureFlags'
import { filterByRole } from '@/lib/role-access'
import type { RoleRequirement } from '@/lib/role-access'

interface GatedCommandItem extends CommandItem {
  /** Hidden from the palette when the tenant has this feature disabled. */
  feature?: FeatureKey
  /** Hidden from the palette for roles below this one. */
  requiredRole?: RoleRequirement
}

const NAV_ITEMS: GatedCommandItem[] = [
  {
    group: 'Pages',
    label: 'Dashboard',
    path: '/',
    icon: <LayoutDashboard className="size-4" />,
  },
  {
    group: 'Clients',
    label: 'All Clients',
    path: '/clients',
    icon: <Users className="size-4" />,
    keywords: ['customers', 'contacts'],
  },
  {
    group: 'Clients',
    label: 'New Client',
    path: '/clients/new',
    icon: <Plus className="size-4" />,
  },
  {
    group: 'Invoices',
    label: 'All Invoices',
    path: '/invoices',
    icon: <FileText className="size-4" />,
  },
  {
    group: 'Invoices',
    label: 'New Invoice',
    path: '/invoices/new',
    icon: <Plus className="size-4" />,
  },
  {
    group: 'Quotes',
    label: 'All Quotes',
    path: '/quotes',
    icon: <FileText className="size-4" />,
    keywords: ['proposals'],
  },
  {
    group: 'Quotes',
    label: 'New Quote',
    path: '/quotes/new',
    icon: <Plus className="size-4" />,
  },
  {
    group: 'Line Items',
    label: 'All Line Items',
    path: '/line-items',
    icon: <Package className="size-4" />,
    keywords: ['products', 'services', 'catalogue'],
  },
  {
    group: 'Line Items',
    label: 'New Line Item',
    path: '/line-items/new',
    icon: <Plus className="size-4" />,
  },
  {
    group: 'Expenses',
    label: 'All Expenses',
    path: '/expenses',
    icon: <Receipt className="size-4" />,
    keywords: ['spending', 'costs'],
  },
  {
    group: 'Expenses',
    label: 'New Expense',
    path: '/expenses/new',
    icon: <Plus className="size-4" />,
  },
  {
    group: 'Reports',
    label: 'Reports',
    path: '/reports',
    icon: <TrendingUp className="size-4" />,
    keywords: ['analytics', 'revenue'],
  },
  {
    group: 'Settings',
    label: 'Settings',
    path: '/settings',
    icon: <Settings className="size-4" />,
  },
  {
    group: 'Settings',
    label: 'Organization',
    path: '/settings/organization',
    icon: <Building2 className="size-4" />,
    keywords: ['business', 'branding', 'payment'],
    requiredRole: 'ADMIN',
  },
  {
    group: 'Settings',
    label: 'Profile Settings',
    path: '/settings/profile',
    icon: <Users className="size-4" />,
    keywords: ['account', 'avatar'],
  },
  {
    group: 'Settings',
    label: 'Security Settings',
    path: '/settings/profile/security',
    icon: <Shield className="size-4" />,
    keywords: ['password', 'sessions'],
  },
  {
    group: 'Settings',
    label: 'Users',
    path: '/settings/users',
    icon: <Users className="size-4" />,
    keywords: ['users', 'members', 'invite'],
    feature: FEATURE.USER_MANAGEMENT,
    requiredRole: 'ADMIN',
  },
]

interface CommandPaletteProps {
  open: boolean
  onClose: () => void
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate()
  const { data: featureToggles } = useFeatureFlags()
  const role = useCurrentTenantRole()

  function handleSelect(path: string) {
    /* CommandItem.path from @trellis/roots is an untyped string, not the route-tree literal
     * union `to` expects — `href` bypasses that check for a value already known to be a
     * valid route (NAV_ITEMS above only lists registered paths). */
    void navigate({ href: path })
  }

  const items = filterByRole(filterByFeature(NAV_ITEMS, featureToggles), role)

  return (
    <BaseCommandPalette
      open={open}
      onClose={onClose}
      items={items}
      onSelect={handleSelect}
    />
  )
}
